<template>
  <div class="offers" v-if="getOffers">
    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="getOffers"
      :loading="isLoading"
      :rowHover="true"
      :lazy="true"
      filterDisplay="row"
      :stateKey="tableStateStore.filterName()"
      :filters.sync="tableStateStore.filters"
      :rows.sync="tableStateStore.pagination.pageSize"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      :first="tableStateStore.skip()"
      stateStorage="local"
      :totalRecords="getOfferCount"
      :paginator="true"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50, 100]"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h4 class="page-title">{{ $t('offers') }}</h4>
          </div>
          <div class="table-header-sums">
            <span
              >{{ $t('sum') }} {{ $t('grossAmount') }}: {{ formatCurrency(sumGrossAmount) }}</span
            >
            <span>{{ $t('sum') }} {{ $t('netAmount') }}: {{ formatCurrency(sumNetAmount) }}</span>
          </div>
          <div class="table-header-right">
            <button class="btn btn-sm btn-inverse" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>

            <button
              v-if="!isReadOnly"
              class="btn btn-sm btn-inverse"
              @click="$emit('create-offer')"
            >
              <i class="fa fa-plus-circle mr-2"></i>
              {{ $t('offer_create') }}
            </button>
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <!-- Offer Number (internal) -->
      <Column
        field="number"
        header="#"
        sortable
        :headerStyle="{ width: '60px' }"
        :bodyStyle="'text-align: center; width: 60px'"
        filterField="number"
      >
        <template #body="slotProps">
          <router-link
            v-if="canOpenOfferPage && slotProps.data && slotProps.data.number !== null"
            :to="{
              name: 'OfferEditPage',
              params: { offerNumber: slotProps.data.number },
            }"
          >
            {{ slotProps.data.number.toString().padStart(4, '0') }}
          </router-link>
          <span v-else-if="slotProps.data && slotProps.data.number !== null">
            {{ slotProps.data.number.toString().padStart(4, '0') }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Offer Number (on the document) -->
      <Column
        field="offerNumber"
        :header="$t('offer_number')"
        sortable
        filterMatchMode="contains"
        filterField="offerNumber"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.offerNumber">
            {{ slotProps.data.offerNumber }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Offer Type -->
      <Column
        field="offerType"
        :header="$t('offerType')"
        sortable
        :headerStyle="{ width: '140px' }"
        :bodyStyle="'text-align: center; width: 140px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <b-badge
            v-if="slotProps.data && slotProps.data.offerType"
            :variant="getOfferTypeColor(slotProps.data.offerType)"
          >
            {{ $t(slotProps.data.offerType) }}
          </b-badge>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.offerTypes"
            :options="enumOptions.offerTypes"
            @input="onCustomFilter('offerTypes', $event)"
            optionLabel="label"
            :placeholder="$t('offerType')"
            display="chip"
            :style="{ 'max-width': '140px' }"
          >
            <template #option="slotProps">
              <b-badge :variant="getOfferTypeColor(slotProps.option.value)">
                {{ $t(slotProps.option.value) }}
              </b-badge>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Project -->
      <Column
        v-if="showProjectColumn"
        field="project"
        :header="$t('project')"
        sortable
        filterField="projectSearch"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.project">
            <router-link
              v-if="slotProps.data.project.number"
              target="_blank"
              :to="{
                name: 'ProjectEditPage',
                params: { projectNumber: slotProps.data.project.number },
              }"
              >{{ slotProps.data.project.number.toString().padStart(4, '0') }}
              <i class="fa fa-sm fa-external-link"></i>
            </router-link>
            &nbsp;-&nbsp;
            {{ slotProps.data.project.customer.firstname }}
            {{ slotProps.data.project.customer.lastname }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('search')"
          />
        </template>
      </Column>

      <!-- Supplier (incoming offers only) -->
      <Column
        field="supplier"
        :header="$t('supplier')"
        sortable
        filterMatchMode="contains"
        filterField="supplierSearch"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.supplier">
            {{ slotProps.data.supplier.name }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Offer Date -->
      <Column
        field="offerDate"
        :header="$t('offerDate')"
        sortable
        :headerStyle="{ width: '120px' }"
        :bodyStyle="'text-align: center; width: 120px'"
        filterField="offerDateRange"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          {{ formatDate(slotProps.data && slotProps.data.offerDate) }}
        </template>
        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.range"
            @input="filterCallback()"
            selectionMode="range"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date_range')"
            :style="{ 'max-width': '150px' }"
          />
        </template>
      </Column>

      <!-- Valid Until -->
      <Column
        field="validUntil"
        :header="$t('validUntil')"
        sortable
        :headerStyle="{ width: '120px' }"
        :bodyStyle="'text-align: center; width: 120px'"
        filterField="validUntilRange"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          {{ formatDate(slotProps.data && slotProps.data.validUntil) }}
        </template>
        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.range"
            @input="filterCallback()"
            selectionMode="range"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date_range')"
            :style="{ 'max-width': '150px' }"
          />
        </template>
      </Column>

      <!-- Editing State -->
      <Column
        field="editingState"
        :header="$t('editing_state')"
        sortable
        :headerStyle="{ width: '140px' }"
        :bodyStyle="'text-align: center; width: 140px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <b-badge
            v-if="slotProps.data && slotProps.data.editingState"
            :variant="getOfferEditingStateColor(slotProps.data.editingState)"
          >
            {{ $t(slotProps.data.editingState) }}
          </b-badge>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.editingStates"
            :options="enumOptions.editingStates"
            @input="onCustomFilter('editingStates', $event)"
            optionLabel="label"
            :placeholder="$t('editing_state')"
            display="chip"
            :style="{ 'max-width': '150px' }"
          >
            <template #option="slotProps">
              <b-badge :variant="getOfferEditingStateColor(slotProps.option.value)">
                {{ $t(slotProps.option.value) }}
              </b-badge>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Customer State -->
      <Column
        field="customerState"
        :header="$t('customer_state')"
        sortable
        :headerStyle="{ width: '140px' }"
        :bodyStyle="'text-align: center; width: 140px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <b-badge
            v-if="slotProps.data && slotProps.data.customerState"
            :variant="getOfferCustomerStateColor(slotProps.data.customerState)"
          >
            {{ $t(slotProps.data.customerState) }}
          </b-badge>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.customerStates"
            :options="enumOptions.customerStates"
            @input="onCustomFilter('customerStates', $event)"
            optionLabel="label"
            :placeholder="$t('customer_state')"
            display="chip"
            :style="{ 'max-width': '150px' }"
          >
            <template #option="slotProps">
              <b-badge :variant="getOfferCustomerStateColor(slotProps.option.value)">
                {{ $t(slotProps.option.value) }}
              </b-badge>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Net Amount -->
      <Column
        field="netAmount"
        filterField="netAmount"
        :header="$t('netAmount')"
        sortable
        :headerStyle="{ width: '120px', 'text-align': 'right' }"
        :bodyStyle="{ 'text-align': 'right' }"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data && slotProps.data.netAmount) }}
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('netAmount')"
          />
        </template>
      </Column>

      <!-- Gross Amount -->
      <Column
        field="grossAmount"
        filterField="grossAmount"
        :header="$t('grossAmount')"
        sortable
        :headerStyle="{ width: '120px', 'text-align': 'right' }"
        :bodyStyle="{ 'text-align': 'right' }"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data && slotProps.data.grossAmount) }}
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('grossAmount')"
          />
        </template>
      </Column>

      <!-- Created By -->
      <Column field="createdBy" :header="$t('createdBy')" sortable filterField="createdBy">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.createdBy">
            {{ slotProps.data.createdBy.firstname }} {{ slotProps.data.createdBy.lastname }}<br />
            <small>{{ slotProps.data.createdBy.email }}</small>
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Updated By -->
      <Column field="updatedBy" :header="$t('updatedBy')" sortable filterField="updatedBy">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.updatedBy">
            {{ slotProps.data.updatedBy.firstname }} {{ slotProps.data.updatedBy.lastname }}<br />
            <small>{{ slotProps.data.updatedBy.email }}</small>
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Actions -->
      <Column
        :header="$t('actions')"
        :headerStyle="{ width: '100px' }"
        :bodyStyle="'text-align: center; width: 100px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <button class="btn" @click="$emit('edit-offer', slotProps.data)" title="Edit Offer">
            <i class="link-icon fa fa-lg fa-edit"></i>
          </button>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import dayjs from 'dayjs';
import Calendar from 'primevue/calendar';
import {
  getOfferCustomerStateColor,
  getOfferEditingStateColor,
  getOfferTypeColor,
} from '@/helpers/colors';

/** Multi-select filter of the list contract → GraphQL enum of its options. */
const ENUM_TYPE_BY_CUSTOM_FILTER = {
  offerTypes: 'OfferType',
  editingStates: 'OfferEditingState',
  customerStates: 'OfferCustomerState',
};

const defaultFilters = () => ({
  number: { value: null },
  offerNumber: { value: null },
  projectSearch: { value: null },
  supplierSearch: { value: null },
  netAmount: { value: null },
  grossAmount: { value: null },
  createdBy: { value: null },
  updatedBy: { value: null },
  offerDateRange: { range: null },
  validUntilRange: { range: null },
});

const defaultCustomFilters = () => ({
  offerTypes: [],
  editingStates: [],
  customerStates: [],
});

/** Offers of the organization with the filters of the list contract (see feature page angebote). */
export default {
  name: 'OffersTable',
  components: {
    DataTable,
    InputText,
    Column,
    MultiSelect,
    Calendar,
  },

  props: {
    tableStatePrefix: {
      type: String,
      default: 'offers',
    },
    /** Viewer on the view offers: lists, cannot create. */
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    /** Number of the project whose block shows the table; the server forces it. */
    projectNumber: {
      type: Number,
      default: null,
    },
    /** The block hides the column: every offer belongs to its project. */
    showProjectColumn: {
      type: Boolean,
      default: true,
    },
    /** Whether the offer number opens the view offers; without a level on it, plain text. */
    canOpenOfferPage: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      offerTableStateConfig: {
        name: this.tableStatePrefix,
        filters: defaultFilters(),
        customFilters: defaultCustomFilters(),
        sorting: { sortField: 'number', sortOrder: -1 }, // Latest offers first
      },
    };
  },

  computed: {
    ...mapGetters(['getOffers', 'getOfferCount']),
    ...mapGetters(['isLoading', 'getTableState', 'getEnumValues']),

    tableStateStore() {
      return this.getTableState(this.offerTableStateConfig.name);
    },

    /** Options per multi-select filter: `{ value, label }` of the enum values. */
    enumOptions() {
      return Object.fromEntries(
        Object.entries(ENUM_TYPE_BY_CUSTOM_FILTER).map(([filterName, enumType]) => [
          filterName,
          this.getEnumValues(enumType).map((value) => ({ value, label: this.$t(value) })),
        ])
      );
    },

    sumGrossAmount() {
      return (this.getOffers || []).reduce((total, offer) => total + (offer.grossAmount || 0), 0);
    },

    sumNetAmount() {
      return (this.getOffers || []).reduce((total, offer) => total + (offer.netAmount || 0), 0);
    },
  },

  methods: {
    ...mapActions(['initTableState', 'fetchEnumValues', 'fetchOffersPaginated']),

    getOfferTypeColor,
    getOfferEditingStateColor,
    getOfferCustomerStateColor,

    /**
     * Load remote table data
     */
    async loadTableData() {
      await this.fetchOffersPaginated({
        projectNumber: this.projectNumber,
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: { ...this.tableStateStore.filters, ...this.tableStateStore.customFilters },
      });
    },

    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    async onFilter() {
      this.loadTableData();
    },

    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },

    /** Multi-select filter `filterName` (offerTypes, editingStates, customerStates) */
    async onCustomFilter(filterName, selectedOptions) {
      this.tableStateStore.setCustomFilters({
        [filterName]: this.enumOptions[filterName].filter((option) =>
          selectedOptions.includes(option)
        ),
      });
      await this.loadTableData();
    },

    formatDate(date) {
      if (!date) return '-';
      return dayjs(date).format('DD.MM.YYYY');
    },

    formatCurrency(amount, currency = 'EUR') {
      if (amount === null || amount === undefined) return '-';
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: currency,
      }).format(amount);
    },

    /**
     * Reset filter settings
     */
    async onRemoveFilters() {
      this.tableStateStore.reset();
      await this.loadTableData();
    },
  },

  async created() {
    await this.initTableState(this.offerTableStateConfig);
  },

  async mounted() {
    await Promise.all(
      Object.values(ENUM_TYPE_BY_CUSTOM_FILTER).map((enumType) => this.fetchEnumValues(enumType))
    );
    await this.loadTableData();
  },
};
</script>

<style lang="scss">
.p-column-filter-clear-button {
  display: none;
  width: 0;
}

.p-column-filter-menu-button {
  display: none;
  width: 0;
}

.table-header {
  display: flex;
  justify-content: space-between;
}

.table-header-left {
  display: flex;
  justify-content: flex-start;
}

.table-header-sums {
  display: flex;
  align-items: start;
  column-gap: 1em;
}

.table-header-right {
  display: flex;
  justify-content: flex-end;
  column-gap: 1em;
}

/** Fix Dropdown overlay and table height */
.p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 37rem;
}
</style>
